import React, { useState, useEffect } from 'react';
import _get from 'lodash/get';
import Loader from 'cccisd-loader';
import Tabs from 'cccisd-tabs';
import { client } from 'cccisd-apollo';
import StudentTab from './StudentTab';
import PeerNomTab from './PeerNomTab';
import ClimateTab from './ClimateTab';
import NominationsTab from './NominationsTab';
import MSNominationsTab from './MSNominationsTab';
import query from './query.graphql';
import style from './style.css';

function Administrative() {
    const [isLoading, setIsLoading] = useState(true);
    const [classesWithNoAp, setClassesWithNoAp] = useState([]);
    const [currTimepoint, setCurrTimepoint] = useState(1);
    const [timepointOptions, setTimepointOptions] = useState([]);
    const [isPnElemTab, setIsPnElemTab] = useState(false);
    const [isClimateElemTab, setIsClimateElemTab] = useState(false);
    const [isClimateMsTab, setIsClimateMsTab] = useState(false);
    const [classIdsWithMsClimateSurvey, setClassIdsWithMsClimateSurvey] = useState([]);

    useEffect(() => {
        async function getTabsData() {
            setIsLoading(true);

            const resp = await client.query({ query });
            const classes = _get(resp, 'data.groups.classList', []) || [];
            const selectedAps = [];
            const classWithNoAp = [];
            const msClimateClassIds = new Set();
            classes.forEach(c => {
                const ap = _get(c, 'selectedAssignmentPlan', null);
                if (!ap || !Array.isArray(ap.sessionList) || ap.sessionList.length <= 0) {
                    const label = _get(c, 'group.label', 'Unnamed');
                    classWithNoAp.push(label);
                }

                if (ap.sessionList.some(session => session.settings.options.some(opt => opt.includes('ms_pn')))) {
                    msClimateClassIds.add(c.group.groupId);
                }

                selectedAps.push(ap);
            });

            setClassesWithNoAp(classWithNoAp);

            let showPnElemTab = false;
            let showClimateElemTab = false;
            let showClimateMsTab = false;
            let timepoints = {}; // map of { <int>: <boolean> }
            selectedAps.forEach(ap => {
                ap.sessionList.forEach(session => {
                    const tp = session.timepoint;
                    if (!tp) {
                        console.error('No timepoint found for assignment session ' + session.sessionId);
                        return;
                    }

                    const surveyHandles = Array.isArray(session?.settings?.options) ? session.settings.options : [];
                    if (surveyHandles.length <= 0) {
                        console.error('No options selected for assignment session ' + session.sessionId);
                        return;
                    }

                    surveyHandles.forEach(surveyHandle => {
                        if (surveyHandle === 'climate') {
                            showClimateElemTab = true;
                        } else if (surveyHandle.startsWith('pn')) {
                            showPnElemTab = true;
                        } else if (surveyHandle === 'climate_ms') {
                            showClimateMsTab = true;
                        }
                    });
                    timepoints[tp] = true;
                });
            });

            setIsPnElemTab(showPnElemTab);
            setIsClimateElemTab(showClimateElemTab);
            setIsClimateMsTab(showClimateMsTab);
            setTimepointOptions(Object.keys(timepoints).map(tp => parseInt(tp, 10)));
            setIsLoading(false);
            setClassIdsWithMsClimateSurvey([...msClimateClassIds]);
        }

        getTabsData();
    }, []);

    function renderTimepointPills() {
        return (
            timepointOptions.length >= 1 && (
                <div className={`btn-group ${style.pillButtons}`} role="group">
                    {timepointOptions
                        .sort((a, b) => a - b)
                        .map(timepoint => (
                            <button
                                key={timepoint}
                                type="button"
                                className={`btn ${currTimepoint === timepoint ? 'btn-primary' : 'btn-secondary'}`}
                                onClick={() => setCurrTimepoint(timepoint)}
                            >
                                Time {timepoint}
                            </button>
                        ))}
                </div>
            )
        );
    }

    function renderStudentTab() {
        return {
            name: 'Students',
            title: 'Students',
            content: <StudentTab renderTimepointPills={renderTimepointPills} currTimepoint={currTimepoint} />,
        };
    }

    function renderPnTab() {
        return {
            name: 'Networker',
            title: 'Networker',
            content: <PeerNomTab renderTimepointPills={renderTimepointPills} currTimepoint={currTimepoint} />,
        };
    }

    function renderNominationsElemTab() {
        return {
            name: 'Elem Nominations',
            title: 'Elem Nominations',
            content: <NominationsTab timepointOptions={timepointOptions} />,
        };
    }

    function renderClimateElemTab() {
        return {
            name: 'Elem Climate',
            title: 'Elem Climate',
            content: <ClimateTab renderTimepointPills={renderTimepointPills} currTimepoint={currTimepoint} />,
        };
    }

    function renderNominationsMsTab() {
        return {
            name: 'MS Nominations',
            title: 'MS Nominations',
            content: (
                <MSNominationsTab
                    renderTimepointPills={renderTimepointPills}
                    currTimepoint={currTimepoint + ''}
                    classIdsWithMsClimateSurvey={classIdsWithMsClimateSurvey}
                />
            ),
        };
    }

    function renderClimateMsTab() {
        return {
            name: 'MS Climate',
            title: 'MS Climate',
            content: <ClimateTab renderTimepointPills={renderTimepointPills} currTimepoint={currTimepoint} isMs />,
        };
    }

    let tabList = [renderStudentTab()];
    if (isPnElemTab) {
        tabList.push(renderPnTab());
        tabList.push(renderNominationsElemTab());
    }
    if (isClimateElemTab) {
        tabList.push(renderClimateElemTab());
    }

    if (classIdsWithMsClimateSurvey.length >= 0) {
        tabList.push(renderNominationsMsTab());
    }
    if (isClimateMsTab) {
        tabList.push(renderClimateMsTab());
    }

    return (
        <Loader loading={isLoading}>
            {classesWithNoAp.length > 0 && (
                <div className="alert alert-warning">
                    <p className="text-warning-emphasis mb-0">
                        There are {classesWithNoAp.length} Classes that have no assignment plan. Contact an
                        administrator to make sure every student has an assessment.
                    </p>
                </div>
            )}
            {tabList.length > 0 && <Tabs tabList={tabList} />}
        </Loader>
    );
}

export default Administrative;
